import {ReportItemTemplateField} from '@domain/report-data-set/report-item-template-field.enum';
import {OverviewMachine} from '@presentation/pages/machine-overview/overview-machine';
import {ReportItem} from '@presentation/pages/machine-overview/report-item';
import {ArrayUtils, AssertionUtils} from '@vdw/angular-component-library';
import {MachineGroupOverviewList} from './sidebar/group-editor/machine-group-overview-list';
import {MachineOverviewList} from './sidebar/group-editor/machine-overview-list';

export class OverviewMachineGroup {
  private _id: number;
  private _name: string;
  private _floorplanId: number;
  private _groups: OverviewMachineGroup[];
  private _machineIds: number[];
  private _machines: OverviewMachine[];
  private _reportItems: ReportItem[];

  public constructor(id: number, name: string, floorplanId: number, groups: OverviewMachineGroup[], machineIds: number[], reportItems: ReportItem[]) {
    this._id = id;
    this._name = name;
    this._floorplanId = floorplanId;
    this._groups = groups;
    this._machineIds = machineIds;
    this._reportItems = reportItems;
  }

  public static fromJSON(machineGroupJSON: any): OverviewMachineGroup {
    return new OverviewMachineGroup(
      machineGroupJSON.id,
      machineGroupJSON.name,
      machineGroupJSON.floorplanId,
      machineGroupJSON.groups.map((subMachineGroupJSON: any) => OverviewMachineGroup.fromJSON(subMachineGroupJSON)),
      machineGroupJSON.machineIds,
      machineGroupJSON.reportItems.map((reportItemJSON: any) => ReportItem.fromJSON(reportItemJSON))
    );
  }

  public get id(): number {
    return this._id;
  }

  public get name(): string {
    return this._name;
  }

  public set name(value: string) {
    this._name = value;
  }

  public get floorplanId(): number {
    return this._floorplanId;
  }

  public set floorplanId(value: number) {
    this._floorplanId = value;
  }

  public get groups(): OverviewMachineGroup[] {
    return this._groups;
  }

  public set groups(value: OverviewMachineGroup[]) {
    this._groups = value;
  }

  public get machineIds(): number[] {
    return this._machineIds;
  }

  public set machineIds(value: number[]) {
    this._machineIds = value;
  }

  public get machines(): OverviewMachine[] {
    return this._machines;
  }

  public set machines(value: OverviewMachine[]) {
    this._machines = value;
  }

  public get reportItems(): ReportItem[] {
    return this._reportItems;
  }

  public set reportItems(value: ReportItem[]) {
    this._reportItems = value;
  }

  public getReportItem(reportItemTemplateField: ReportItemTemplateField): ReportItem {
    return this._reportItems.find((reportItem: ReportItem) => reportItem.field === reportItemTemplateField);
  }

  public addOrUpdateReportItem(reportItemToAddOrUpdate: ReportItem): void {
    this._reportItems = this._reportItems.filter((reportItem: ReportItem) => reportItem.field !== reportItemToAddOrUpdate.field);
    this._reportItems.push(reportItemToAddOrUpdate);
  }

  public getNestedChildMachineIds(): number[] {
    const nestedChildMachineIds: number[] = [];

    if (!AssertionUtils.isNullOrUndefined(this._machineIds)) {
      nestedChildMachineIds.push(...this._machineIds);
    }

    if (!AssertionUtils.isNullOrUndefined(this._groups)) {
      nestedChildMachineIds.push(...this._groups.flatMap((group: OverviewMachineGroup) => group.getNestedChildMachineIds()));
    }

    return ArrayUtils.distinctBy([...nestedChildMachineIds], (machineId: number) => machineId);
  }

  public getNestedChildGroupIds(): number[] {
    const nestedChildGroupIds: number[] = [];

    if (!AssertionUtils.isNullOrUndefined(this._groups)) {
      nestedChildGroupIds.push(...this._groups.map((group: OverviewMachineGroup) => group.id), this.id);
    }

    if (!AssertionUtils.isNullOrUndefined(this._groups)) {
      nestedChildGroupIds.push(...this._groups.flatMap((group: OverviewMachineGroup) => group.getNestedChildGroupIds()));
    }

    return ArrayUtils.distinctBy([...nestedChildGroupIds], (groupId: number) => groupId);
  }

  public static fromMachineGroupOverviewList(machineGroupOverviewList: MachineGroupOverviewList): OverviewMachineGroup {
    return new OverviewMachineGroup(
      machineGroupOverviewList.id,
      machineGroupOverviewList.name,
      null,
      machineGroupOverviewList.groups?.map((group: MachineGroupOverviewList) => OverviewMachineGroup.fromMachineGroupOverviewList(group)),
      machineGroupOverviewList.machines?.map((machine: MachineOverviewList) => machine.id),
      []
    );
  }
}
